<template>
  <div class="container-fluid mb-5">
    <div class="row starter-main mt-5 pt-5">
      <div class="col-12 mb-4 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center gap-3">
          <button class="btn btn-outline-secondary btn-sm" @click="goBack">
            <i class="fas fa-arrow-left me-1"></i> Back
          </button>
          <h4 class="mb-0">Ticket #{{ ticketId.slice(-6).toUpperCase() }}</h4>
        </div>
        <div class="d-flex gap-2" v-if="ticket">
          <span class="font-weight-bold" :class="getStatusBadgeClass(ticket.status)">{{ ticket.status.toUpperCase() }}</span>
          <span class="font-weight-bold" :class="getPriorityBadgeClass(ticket.priority)">{{ ticket.priority.toUpperCase() }}</span>
        </div>
      </div>

      <div v-if="isLoading" class="col-12 text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else-if="ticket" class="row g-4">
        <!-- Ticket Information -->
        <div class="col-lg-8">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-header bg-white border-bottom-0 pt-4 px-4">
              <h5 class="card-title fw-bold text-primary">Ticket Information</h5>
            </div>
            <div class="card-body px-4 pb-4">
              <div class="row g-4">
                <div class="col-md-6">
                  <label class="small text-muted d-block mb-1">Name</label>
                  <p class="fw-medium mb-0">{{ ticket.name }}</p>
                </div>
                <div class="col-md-6">
                  <label class="small text-muted d-block mb-1">Email</label>
                  <p class="fw-medium mb-0">{{ ticket.email }}</p>
                </div>
                <div class="col-12">
                  <label class="small text-muted d-block mb-1">Subject</label>
                  <p class="fw-bold mb-0 text-dark" style="font-size: 1.1rem;">{{ ticket.subject }}</p>
                </div>
                <div class="col-12">
                  <label class="small text-muted d-block mb-1">Message</label>
                  <div class="p-3 bg-light rounded text-dark" style="white-space: pre-wrap; line-height: 1.6;">
                    {{ ticket.message }}
                  </div>
                </div>
                <div class="col-md-4">
                  <label class="small text-muted d-block mb-1">Category</label>
                  <span class="badge bg-secondary font-weight-normal">{{ ticket.category }}</span>
                </div>
                <div class="col-md-4">
                  <label class="small text-muted d-block mb-1">Created At</label>
                  <p class="small mb-0">{{ formatDate(ticket.created_at) }}</p>
                </div>
                <div class="col-12" v-if="ticket.attachments && ticket.attachments.length">
                  <label class="small text-muted d-block mb-2">Attachments</label>
                  <div class="d-flex flex-wrap gap-2">
                    <a v-for="(file, idx) in ticket.attachments" 
                       :key="idx" 
                       :href="file" 
                       target="_blank"
                       class="btn btn-outline-info btn-sm d-inline-flex align-items-center gap-2"
                       download>
                      <i class="fas fa-paperclip"></i> Attachment {{ typeof idx === 'number' && idx + 1  }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Admin Actions -->
        <div class="col-lg-4 d-flex">
          <div class="card shadow-sm border-0 sticky-top w-100 mb-0" style="top: 100px;">
            <div class="card-header bg-white border-bottom-0 pt-4 px-4">
              <h5 class="card-title fw-bold text-success">Admin Actions</h5>
            </div>
            <div class="card-body px-4 pb-4">
              <form v-if="!isReadOnly" @submit.prevent="handleUpdate">
                <div class="mb-3">
                  <label class="form-label small text-muted">Status</label>
                  <select class="form-select" v-model="updateData.status">
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div class="mb-3">
                  <label class="form-label small text-muted">Priority</label>
                  <select class="form-select" v-model="updateData.priority">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div class="mb-4">
                  <label class="form-label small text-muted">Admin Reply</label>
                  <textarea 
                    class="form-control" 
                    rows="6" 
                    v-model="updateData.admin_reply"
                    placeholder="Type your response here..."
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  class="btn btn-primary w-100 py-2 fw-bold shadow-sm"
                  :disabled="isUpdating"
                >
                  <span v-if="isUpdating" class="spinner-border spinner-border-sm me-2"></span>
                  Update Ticket
                </button>
              </form>
              <div v-else>
                <div class="mb-4">
                  <label class="small text-muted d-block mb-1">Status</label>
                  <span :class="getStatusBadgeClass(ticket.status)">{{ ticket.status.toUpperCase() }}</span>
                </div>
                <div class="mb-4">
                  <label class="small text-muted d-block mb-1">Priority</label>
                  <span :class="getPriorityBadgeClass(ticket.priority)">{{ ticket.priority.toUpperCase() }}</span>
                </div>
                <div class="mb-4">
                  <label class="small text-muted d-block mb-1">Admin Reply</label>
                  <div class="p-3 bg-light rounded text-dark" style="white-space: pre-wrap;">
                    {{ ticket.admin_reply || 'No reply provided yet.' }}
                  </div>
                </div>
              </div>
              
              <div v-if="ticket.replied_at" class="mt-4 p-3 bg-light rounded border-start border-4 border-info">
                <label class="small text-muted d-block mb-1">Last Replied By</label>
                <p class="small mb-1 font-weight-bold text-black">{{   'Admin' }}</p>
                <p class="small text-muted mb-0">{{ formatDate(ticket.replied_at) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="col-12 text-center py-5">
        <h5 class="text-muted">Ticket not found or an error occurred.</h5>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiService from "@/services/apiService";
import { formatDate } from "@/composables/FormatDate";
import { toast, Content } from "vue3-toastify";

const props = defineProps({
  id: {
    type: String,
    required: false
  },
  readOnly: {
    type: Boolean,
    default: false
  }
});

const route = useRoute();
const router = useRouter();
const ticketId = computed(() => props.id || (route.params.id as string));

const ticket = ref<any>(null);
const isLoading = ref(true);
const isUpdating = ref(false);

const isReadOnly = computed(() => props.readOnly || route.query.mode === 'view');

const updateData = ref({
  status: "open",
  priority: "medium",
  admin_reply: ""
});

const fetchTicketDetails = async () => {
  isLoading.value = true;
  try {
    const res = await apiService.getById("/common/support/tickets", ticketId.value);
    ticket.value = res;
    // Pre-fill update form with current values
    updateData.value.status = res.status;
    updateData.value.priority = res.priority;
    updateData.value.admin_reply = res.admin_reply || "";
  } catch (err: any) {
    console.error("Failed to fetch ticket details:", err);
    toast.error("Failed to load ticket details", { theme: "colored" });
  } finally {
    isLoading.value = false;
  }
};

const handleUpdate = async () => {
  isUpdating.value = true;
  try {
    await apiService.put("/common/admin/support/tickets", ticketId.value, updateData.value);
    toast.success("Ticket updated successfully", { theme: "colored" });
    await fetchTicketDetails();
  } catch (err: any) {
    console.error("Failed to update ticket:", err);
    toast.error(err.response?.data?.message || "Failed to update ticket", { theme: "colored" });
  } finally {
    isUpdating.value = false;
  }
};

const goBack = () => {
  if (props.readOnly || route.query.mode === 'view') {
    const basePath = props.readOnly ? '/closed-tickets' : '/open-tickets';
    router.push(basePath);
  } else {
    router.push("/open-tickets");
  }
};

watch(ticketId, () => {
  fetchTicketDetails();
});

const getStatusBadgeClass = (status: string) => {
  const base = "badge px-3 py-2 rounded-pill ";
  switch (status) {
    case 'open': return base + "bg-danger";
    case 'in-progress': return base + "bg-warning text-dark";
    case 'resolved': return base + "bg-success";
    case 'closed': return base + "bg-secondary";
    default: return base + "bg-light text-dark";
  }
};

const getPriorityBadgeClass = (priority: string) => {
  const base = "badge px-3 py-2 rounded-pill ";
  switch (priority) {
    case 'high': return base + "bg-danger";
    case 'medium': return base + "bg-info";
    case 'low': return base + "bg-light text-dark border";
    default: return base + "bg-light text-dark";
  }
};

onMounted(() => {
  fetchTicketDetails();
});
</script>

<style scoped>
.card {
  transition: transform 0.2s;
}
.form-select, .form-control {
  border-radius: 8px;
  border-color: #dee2e6;
}
.form-select:focus, .form-control:focus {
  border-color: #21c1d6;
  box-shadow: 0 0 0 0.2rem rgba(33, 193, 214, 0.25);
}
.badge {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}
</style>
